import { useState, useEffect } from "react";
import { createLibraryItem, updateLibraryItem } from "@/apiClient";
import { getDefaultValues } from "@/lib/libraryConfig";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Loader2, Plus, X } from "lucide-react";
// Phase 7++++ — buffet contexts (live_stations, standard_stations,
// theme_buffets.{hot_kitchen|cold_kitchen|pastry|lines}) now use the
// new Dish + BuffetStation shape. The two reusable editors live in
// /components/mealplan and are imported here so the library form
// dialog renders the same compact UI as the per-day meal-plan editor.
import DishListEditor from "@/components/mealplan/DishListEditor";
import StationsEditor from "@/components/mealplan/StationsEditor";

const REPEAT_TYPES = [
  { value: "every_day", label: "Every Day" },
  { value: "sea_days", label: "Sea Days" },
  { value: "port_days", label: "Port Days" },
  { value: "specific_days", label: "Specific Days" },
];

const arrToText = (a) => (Array.isArray(a) ? a : []).join("\n");
// IMPORTANT: do NOT trim each line here — see textListHelpers.js for the
// full rationale. Trimming on every keystroke eats spaces while typing
// (e.g. "Smoked Salmon" → "SmokedSalmon"). The backend Pydantic
// validators trim & filter at save time anyway.
const textToArr = (t) =>
  (t || "")
    .split(/\r?\n/)
    .filter((s) => s.trim().length > 0);

/**
 * Generic library form dialog. Restaurants now have their own
 * `RestaurantFormDialog` (per-type fields differ enough to warrant a
 * dedicated component). LibraryPage dispatches based on
 * `config.customForm` — restaurants never reach this file.
 *
 * Phase 7++++ — accepts auxiliary library lists used by the new
 * `dishList` / `stations` / `buffetLines` renderers so the buffet UX
 * inside the library matches the per-day editor.
 */
export default function LibraryFormDialog({
  open,
  onOpenChange,
  libraryName,
  config,
  editingItem,
  onSuccess,
  // Phase 7++++ — auxiliary libs (caller fetches them lazily). All
  // default to [] so the dialog renders fine when the picker UX is
  // not relevant (e.g. editing a Ship or a Target).
  materialsLib = [],
  liveStationsLib = [],
  standardStationsLib = [],
  // Phase 9 — canapé form needs three more libs for its libSelect
  // pickers (serving material, production area, person in charge).
  // The materialsLib above is reused for serving material.
  productionAreasLib = [],
  staffLib = [],
  // Phase 12 — special_events form needs the new groups lib.
  specialEventGroupsLib = [],
  // Phase 15 (F1) — special_events form has a new "Linked Canapés"
  // multi-select sourced from lib_canapes.
  canapesLib = [],
}) {
  const isEdit = !!editingItem;
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (editingItem) {
        const clone = { ...editingItem };
        delete clone.id;
        delete clone.created_at;
        delete clone.updated_at;
        setFormData(clone);
      } else {
        setFormData(getDefaultValues(libraryName));
      }
      setError(null);
    }
  }, [open, editingItem, libraryName]);

  const handleChange = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = { ...formData };
    if (payload.repeat_rule && payload.repeat_rule.type !== "specific_days") {
      payload.repeat_rule = { ...payload.repeat_rule, days: null };
    }
    try {
      if (isEdit) {
        await updateLibraryItem(libraryName, editingItem.id, payload);
      } else {
        await createLibraryItem(libraryName, payload);
      }
      onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Save failed";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSaving(false);
    }
  };

  // ── List field helpers ──────────────────────────────
  const addListItem = (key) => {
    const current = formData[key] || [];
    handleChange(key, [...current, ""]);
  };
  const updateListItem = (key, index, value) => {
    const current = [...(formData[key] || [])];
    current[index] = value;
    handleChange(key, current);
  };
  const removeListItem = (key, index) => {
    const current = [...(formData[key] || [])];
    current.splice(index, 1);
    handleChange(key, current);
  };

  // ── Repeat rule helpers ─────────────────────────────
  const handleRepeatType = (type) => {
    const rule = { type, days: type === "specific_days" ? [] : null };
    handleChange("repeat_rule", rule);
  };
  const toggleDay = (dayNum) => {
    const rule = formData.repeat_rule || { type: "specific_days", days: [] };
    const current = rule.days || [];
    const next = current.includes(dayNum)
      ? current.filter((d) => d !== dayNum)
      : [...current, dayNum].sort((a, b) => a - b);
    handleChange("repeat_rule", { ...rule, days: next });
  };

  const renderField = (field) => {
    const val = formData[field.key];
    const testIdBase = `field-${field.key}`;

    switch (field.type) {
      case "text":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Input
              id={testIdBase}
              value={val || ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              data-testid={
                field.key === "name"
                  ? "form-name-input"
                  : field.key === "unit"
                    ? "form-unit-input"
                    : testIdBase
              }
            />
          </div>
        );

      case "textarea":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Textarea
              id={testIdBase}
              value={val || ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
              data-testid={testIdBase}
            />
          </div>
        );

      case "select":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Select
              value={val ? val : "__empty__"}
              onValueChange={(v) =>
                handleChange(field.key, v === "__empty__" ? null : v)
              }
            >
              <SelectTrigger id={testIdBase} data-testid={testIdBase}>
                <SelectValue placeholder={field.placeholder || "Select…"} />
              </SelectTrigger>
              <SelectContent>
                {(field.options || []).map((opt) => (
                  <SelectItem
                    key={opt.value || "__empty__"}
                    value={opt.value || "__empty__"}
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "number":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Input
              id={testIdBase}
              type="number"
              value={val ?? ""}
              onChange={(e) =>
                handleChange(field.key, parseInt(e.target.value, 10) || 0)
              }
              min={field.min}
              max={field.max}
              required={field.required}
              data-testid={testIdBase}
            />
          </div>
        );

      case "boolean":
        return (
          <div
            key={field.key}
            className="flex items-center justify-between rounded-md border px-3 py-2"
          >
            <Label htmlFor={testIdBase} className="cursor-pointer">
              {field.label}
            </Label>
            <Switch
              id={testIdBase}
              checked={!!val}
              onCheckedChange={(c) => handleChange(field.key, c)}
              data-testid={testIdBase}
            />
          </div>
        );

      case "color": {
        // Phase 12 — simple HTML5 colour picker. Stores the hex string.
        // Pair with a text input for users who prefer to paste a hex.
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id={testIdBase}
                value={(val || "#3B82F6").slice(0, 7)}
                onChange={(e) => handleChange(field.key, e.target.value)}
                className="h-8 w-12 cursor-pointer rounded border border-input bg-background"
                data-testid={testIdBase}
              />
              <Input
                type="text"
                value={val || ""}
                onChange={(e) => handleChange(field.key, e.target.value || null)}
                placeholder={field.placeholder || "#RRGGBB"}
                className="h-8 max-w-[120px] text-xs"
                data-testid={`${testIdBase}-hex`}
              />
            </div>
          </div>
        );
      }


      case "time":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Input
              id={testIdBase}
              type="time"
              value={val || ""}
              onChange={(e) => handleChange(field.key, e.target.value)}
              required={field.required}
              data-testid={testIdBase}
            />
          </div>
        );

      case "list":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label>{field.label}</Label>
            <div className="space-y-1.5">
              {(val || []).map((item, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <Input
                    value={item}
                    onChange={(e) =>
                      updateListItem(field.key, idx, e.target.value)
                    }
                    placeholder={field.placeholder}
                    data-testid={`${testIdBase}-${idx}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeListItem(field.key, idx)}
                    data-testid={`remove-${field.key}-${idx}`}
                    className="shrink-0 text-destructive hover:bg-destructive/10"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addListItem(field.key)}
                data-testid={`add-${field.key}-btn`}
              >
                <Plus className="h-3.5 w-3.5" />
                Add
              </Button>
            </div>
          </div>
        );

      case "multilineList":
        // One item per line. Stored as List[str] on the wire.
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Textarea
              id={testIdBase}
              value={arrToText(val)}
              onChange={(e) =>
                handleChange(field.key, textToArr(e.target.value))
              }
              placeholder={field.placeholder || "One item per line"}
              rows={5}
              data-testid={testIdBase}
              className="font-mono text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              {Array.isArray(val) ? val.length : 0} item
              {(Array.isArray(val) ? val.length : 0) === 1 ? "" : "s"}
            </p>
          </div>
        );

      case "buffetLines": {
        // Dynamic list of named buffet stations (e.g. "Line 1 — Pasta").
        // Each line: { name: string, items: List[Dish] }.
        // Phase 7++++ — items are Dish objects with per-dish material
        // tag (was: List[str] textarea pre-Turn-2).
        const lines = Array.isArray(val) ? val : [];
        const updateLine = (idx, patch) =>
          handleChange(
            field.key,
            lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)),
          );
        const removeLine = (idx) =>
          handleChange(
            field.key,
            lines.filter((_, i) => i !== idx),
          );
        const addLine = () =>
          handleChange(field.key, [
            ...lines,
            { name: `Line ${lines.length + 1}`, items: [] },
          ]);
        return (
          <div key={field.key} className="space-y-2" data-testid={testIdBase}>
            <div className="flex items-center justify-between">
              <Label>{field.label}</Label>
              <span className="text-[10px] text-muted-foreground">
                Physical buffet stations / lines. Each can have its own name +
                items.
              </span>
            </div>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <div
                  key={idx}
                  className="rounded-md border bg-card p-2.5 space-y-2"
                  data-testid={`${testIdBase}-row-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    <Input
                      value={ln.name || ""}
                      onChange={(e) =>
                        updateLine(idx, { name: e.target.value })
                      }
                      placeholder="Line name (e.g. Line 1 — Pasta Station)"
                      className="h-8 text-sm"
                      data-testid={`${testIdBase}-name-${idx}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLine(idx)}
                      className="shrink-0 text-destructive hover:text-destructive"
                      data-testid={`${testIdBase}-remove-${idx}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <DishListEditor
                    value={ln.items || []}
                    onChange={(next) => updateLine(idx, { items: next })}
                    materialsLib={materialsLib}
                    tidPrefix={`${testIdBase}-items-${idx}`}
                    compact
                  />
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLine}
              data-testid={`add-${field.key}-btn`}
            >
              <Plus className="h-3.5 w-3.5" />
              Add line
            </Button>
          </div>
        );
      }

      // Phase 7++++ — list of Dish objects with material picker per row.
      case "dishList":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label>{field.label}</Label>
            <DishListEditor
              value={Array.isArray(val) ? val : []}
              onChange={(next) => handleChange(field.key, next)}
              materialsLib={materialsLib}
              tidPrefix={testIdBase}
            />
          </div>
        );

      // Phase 7++++ — list of BuffetStation cards (hot/cold/pastry).
      case "stations":
        return (
          <div key={field.key} className="space-y-1.5">
            <Label>{field.label}</Label>
            <StationsEditor
              value={Array.isArray(val) ? val : []}
              onChange={(next) => handleChange(field.key, next)}
              liveStationsLib={liveStationsLib}
              standardStationsLib={standardStationsLib}
              materialsLib={materialsLib}
              tidPrefix={testIdBase}
              accent={field.accent || "slate"}
              label={null}
              // B6 — in the Library context there is no ship → no
              // ship_lines. The Line/Island dropdown would always read
              // "Unassigned" with no options, which looks broken to
              // the user. Hide it; assignment is a voyage-day decision.
              showLinePicker={false}
            />
          </div>
        );

      case "repeatRule": {
        // Phase 7+++ — when the config marks repeat_rule as OPTIONAL,
        // the field becomes opt-in via an "Auto-add to voyages" switch.
        // Switch OFF (default for new docs) → val is null, picker hidden.
        // Switch ON → seed { type:"every_day" } and surface the picker.
        const optInMode = field.required === false;
        const autoAddOn = !!val;
        const rule = val || { type: "every_day", days: null };
        const DAY_CHIPS = Array.from({ length: 14 }, (_, i) => i + 1);
        const selectedDays = rule.days || [];
        const toggleAutoAdd = (on) => {
          handleChange(
            field.key,
            on ? { type: "every_day", days: null } : null,
          );
        };
        return (
          <div key={field.key} className="space-y-2">
            {optInMode && (
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div className="flex flex-col">
                  <Label htmlFor={`${testIdBase}-auto-add`} className="cursor-pointer">
                    Auto-add to voyages
                  </Label>
                  <span className="text-[10px] text-muted-foreground">
                    When off, this event must be added manually from the daily editor.
                  </span>
                </div>
                <Switch
                  id={`${testIdBase}-auto-add`}
                  checked={autoAddOn}
                  onCheckedChange={toggleAutoAdd}
                  data-testid={`${testIdBase}-auto-add`}
                />
              </div>
            )}
            {(!optInMode || autoAddOn) && (
              <>
                <Label>{field.label}</Label>
                <Select value={rule.type} onValueChange={handleRepeatType}>
                  <SelectTrigger data-testid="field-repeat-rule-type">
                    <SelectValue placeholder="Select repeat type" />
                  </SelectTrigger>
                  <SelectContent>
                    {REPEAT_TYPES.map((rt) => (
                      <SelectItem key={rt.value} value={rt.value}>
                        {rt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rule.type === "specific_days" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">
                      Select voyage days (1–14)
                    </Label>
                    <div
                      className="flex flex-wrap gap-1.5"
                      data-testid="day-chip-grid"
                    >
                      {DAY_CHIPS.map((day) => {
                        const selected = selectedDays.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleDay(day)}
                            aria-pressed={selected}
                            data-selected={selected || undefined}
                            data-testid={`form-day-chip-${day}`}
                            className={cn(
                              "inline-flex h-8 w-8 items-center justify-center rounded-md border text-sm font-medium transition-colors",
                              selected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-input bg-background hover:bg-accent"
                            )}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }

      // -------------------------------------------------------------
      // Phase 9 — Canapé Function field types
      // -------------------------------------------------------------
      case "photo": {
        // Local file → POST /api/uploads/canape-photo → save URL to form.
        const onPick = async (e) => {
          const file = e.target?.files?.[0];
          if (!file) return;
          try {
            const fd = new FormData();
            fd.append("file", file);
            const res = await fetch(
              `${process.env.REACT_APP_BACKEND_URL || ""}/api/uploads/canape-photo`,
              {
                method: "POST",
                body: fd,
                // Auth token via headers (mirrors apiClient interceptor).
                headers: localStorage.getItem("access_token")
                  ? { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
                  : {},
              },
            );
            if (!res.ok) {
              const body = await res.text();
              throw new Error(`Upload failed: ${res.status} ${body.slice(0, 200)}`);
            }
            const data = await res.json();
            handleChange(field.key, data.url);
          } catch (err) {
            console.warn("LibraryFormDialog canapé-photo upload failed", err);
            // eslint-disable-next-line no-alert
            alert(`Could not upload the photo:\n${err.message || err}`);
          } finally {
            // Reset the input so picking the same file twice still fires onChange.
            e.target.value = "";
          }
        };
        return (
          <div key={field.key} className="space-y-1.5" data-testid={testIdBase}>
            <Label>{field.label}</Label>
            <div className="flex items-center gap-3">
              {val ? (
                <img
                  src={val}
                  alt={field.label}
                  className="h-20 w-20 rounded border object-cover"
                  data-testid={`${testIdBase}-thumb`}
                />
              ) : (
                <div
                  className="h-20 w-20 rounded border bg-muted/40 flex items-center justify-center text-xs text-muted-foreground"
                  data-testid={`${testIdBase}-placeholder`}
                >
                  no photo
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label
                  className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground cursor-pointer hover:bg-primary/90"
                  data-testid={`${testIdBase}-pick`}
                >
                  {val ? "Replace…" : "Upload Photo"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onPick}
                    className="hidden"
                  />
                </label>
                {val && (
                  <Button
                    type="button" size="sm" variant="ghost"
                    onClick={() => handleChange(field.key, "")}
                    data-testid={`${testIdBase}-clear`}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      }

      case "ingredientList": {
        const rows = Array.isArray(val) ? val : [];
        const updateRow = (idx, patch) => {
          const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
          handleChange(field.key, next);
        };
        const removeRow = (idx) => {
          handleChange(field.key, rows.filter((_, i) => i !== idx));
        };
        const addRow = () => {
          handleChange(field.key, [...rows, {
            // Stable client-side key (Phase 8 code-quality pattern).
            _key: (typeof crypto !== "undefined" && crypto.randomUUID)
              ? crypto.randomUUID()
              : `i_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            name: "", amount: "",
          }]);
        };
        // Enter on the amount field appends a new row (mirrors DishListEditor UX).
        const onAmountKeyDown = (idx) => (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            addRow();
          }
        };
        return (
          <div key={field.key} className="space-y-1.5" data-testid={testIdBase}>
            <Label>{field.label}</Label>
            <div className="flex flex-col gap-1.5">
              {rows.length === 0 && (
                <p className="text-xs text-muted-foreground">No ingredients yet.</p>
              )}
              {rows.map((r, idx) => (
                <div
                  key={r._key || `legacy-ing-${idx}`}
                  className="flex items-center gap-1.5"
                  data-testid={`${testIdBase}-row-${idx}`}
                >
                  {/* Phase 13.2 — display-only 1-based index. Never
                      stored in `ingredients[]`; recomputed on each
                      render so add/remove keeps the numbering tight. */}
                  <span
                    className="shrink-0 w-6 text-right tabular-nums text-[11px] font-medium text-slate-400"
                    data-testid={`${testIdBase}-row-${idx}-num`}
                  >
                    {idx + 1}.
                  </span>
                  <Input
                    value={r.name || ""}
                    onChange={(e) => updateRow(idx, { name: e.target.value })}
                    placeholder="Ingredient"
                    className="flex-1"
                    data-testid={`${testIdBase}-row-${idx}-name`}
                  />
                  <Input
                    value={r.amount || ""}
                    onChange={(e) => updateRow(idx, { amount: e.target.value })}
                    onKeyDown={onAmountKeyDown(idx)}
                    placeholder="Amount (e.g. 5 g)"
                    className="w-32"
                    data-testid={`${testIdBase}-row-${idx}-amount`}
                  />
                  <Button
                    type="button" size="icon" variant="ghost"
                    onClick={() => removeRow(idx)}
                    data-testid={`${testIdBase}-row-${idx}-remove`}
                    aria-label="Remove ingredient"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button" size="sm" variant="outline"
                onClick={addRow}
                data-testid={`${testIdBase}-add`}
                className="self-start"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Ingredient
              </Button>
            </div>
          </div>
        );
      }

      case "libMultiSelect": {
        // Phase 15 (F1) — chip-style multi-select sourced from a
        // library (e.g. `canapes`). Value persists as `string[]`. The
        // user clicks chips to toggle membership.
        const propMap = {
          canapes: canapesLib,
          buffet_materials: materialsLib,
          production_areas: productionAreasLib,
          staff: staffLib,
          special_event_groups: specialEventGroupsLib,
        };
        const options = propMap[field.lib] || [];
        const selected = Array.isArray(val) ? val : [];
        const toggle = (id) => {
          if (selected.includes(id)) {
            handleChange(field.key, selected.filter((x) => x !== id));
          } else {
            handleChange(field.key, [...selected, id]);
          }
        };
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            {options.length === 0 ? (
              <p className="text-xs italic text-muted-foreground"
                 data-testid={`${testIdBase}-empty`}>
                No {field.lib.replace("_", " ")} available — add some in
                the Library first.
              </p>
            ) : (
              <div
                id={testIdBase}
                data-testid={testIdBase}
                className="flex flex-wrap gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-2"
              >
                {options.map((opt) => {
                  const on = selected.includes(opt.id);
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggle(opt.id)}
                      data-testid={`${testIdBase}-chip-${opt.id}`}
                      className={
                        "rounded-full px-2 py-1 text-[11px] font-medium transition " +
                        (on
                          ? "bg-amber-500 text-white shadow"
                          : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-100")
                      }
                    >
                      {on && <span className="mr-1">✓</span>}
                      {opt.name}
                    </button>
                  );
                })}
              </div>
            )}
            {selected.length > 0 && (
              <p className="text-[10px] text-slate-500"
                 data-testid={`${testIdBase}-count`}>
                {selected.length} selected.
              </p>
            )}
          </div>
        );
      }

      case "libSelect": {
        // Foreign-key picker that snapshots the chosen item's name into
        // `formData[field.nameField]` alongside the id. The list comes from
        // a prop named `${field.lib}Lib` (e.g., `productionAreasLib`,
        // `staffLib`, `materialsLib`).
        const propMap = {
          buffet_materials: materialsLib,
          production_areas: productionAreasLib,
          staff: staffLib,
          // Phase 12 — group picker on the Special Events form.
          special_event_groups: specialEventGroupsLib,
        };
        const options = propMap[field.lib] || [];
        // Phase 16 (F15) — Staff entries can carry an optional
        // `deck_phone` field; surface it in the dropdown as a muted
        // `· ext NNNN` suffix so the user can pick the right "John"
        // out of multiple Johns without opening the staff library.
        const optLabel = (opt) => {
          if (field.lib === "staff" && opt && opt.deck_phone) {
            return `${opt.name || ""} · ext ${opt.deck_phone}`;
          }
          return opt?.name || "";
        };
        const onSelect = (next) => {
          if (next === "__empty__") {
            handleChange(field.key, null);
            if (field.nameField) handleChange(field.nameField, "");
            // Phase 13.4 — clear the optional role snapshot too.
            if (field.roleField) handleChange(field.roleField, "");
            return;
          }
          handleChange(field.key, next);
          const lib = options.find((o) => o.id === next);
          if (field.nameField) handleChange(field.nameField, lib?.name || "");
          // Phase 13.4 — snapshot the staff role too. Looks for the
          // `role` field on the picked staff entry; empty string if
          // unset. Drift-tolerant — old picks without a role stay
          // intact on subsequent renders.
          if (field.roleField) handleChange(field.roleField, lib?.role || "");
        };
        return (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={testIdBase}>{field.label}</Label>
            <Select
              value={val ? val : "__empty__"}
              onValueChange={onSelect}
            >
              <SelectTrigger id={testIdBase} data-testid={testIdBase}>
                <SelectValue placeholder={field.placeholder || "Select…"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__empty__">— None —</SelectItem>
                {options.map((opt) => (
                  <SelectItem
                    key={opt.id}
                    value={opt.id}
                    data-testid={`${testIdBase}-opt-${opt.id}`}
                  >
                    {optLabel(opt)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const singular = (config.addLabel || `Add ${config.label}`).replace(/^Add\s+/i, "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        lockOutsideClick
        className="max-h-[85vh] overflow-y-auto sm:max-w-lg"
        data-testid="library-form-dialog"
      >
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            // Phase 7+/Crew-Mess-Buffet hardening — Enter inside ANY
            // <input> field (e.g. Station Name) used to submit the
            // form because that's the HTML default. Now the only way
            // to submit is by clicking the Create/Save button or
            // pressing Enter on the submit button itself. Textareas
            // remain unaffected (Enter inserts a newline there). The
            // DishListEditor's own Enter handler still runs first and
            // appends the next-dish row.
            if (
              e.key === "Enter"
              && e.target instanceof HTMLInputElement
              && e.target.type !== "submit"
              && !e.defaultPrevented
            ) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle data-testid="form-dialog-title">
              {isEdit ? "Edit" : "Create"} {singular}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? `Update the ${singular.toLowerCase()} details below.`
                : `Fill in the details to create a new ${singular.toLowerCase()}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 space-y-4">{config.fields.map(renderField)}</div>

          {error && (
            <div
              className="mb-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
              data-testid="form-error"
            >
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="form-cancel-btn"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="form-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save Changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
